import { useMemo, useState } from "react";
import { Box, ButtonBase, Checkbox, Stack, Switch, Tooltip, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { grades } from "data/grades";
import { useGroup } from "../hooks";
import GroupPreferenceDrawer from "./GroupPreferenceDrawer";

// UI-only preference keys until the group preferences API is available.
const preferenceFeatures = [
  {
    id: "disable-public-collection-sharing",
    feature: "Disable Sharing of Public Collections",
    description: "Disable the ability to share public collections for the selected grades and audiences.",
    restrictedLabel: "Disabled",
    unrestrictedLabel: "Enabled",
  },
  {
    id: "disable-reading-levels",
    feature: "Disable Reading Levels",
    description: "Disable access to the Reading Levels feature for the selected grades and audiences.",
    restrictedLabel: "Disabled",
    unrestrictedLabel: "Enabled",
  },
  {
    id: "hide-search-transparency-info",
    feature: "Hide Search Transparency Info",
    description: "Hide search transparency information for the selected grades and audiences.",
    restrictedLabel: "Hidden",
    unrestrictedLabel: "Visible",
  },
  {
    id: "disable-quick-search",
    feature: "Disable Quick Search",
    description: "Disable access to Quick Search for the selected grades and audiences.",
    restrictedLabel: "Disabled",
    unrestrictedLabel: "Enabled",
  },
];

export default function GroupPreferencesTable() {
  const { group, isGroupLoading } = useGroup(false);
  const [draftPreferences, setDraftPreferences] = useState({});
  const [selectedFeatureId, setSelectedFeatureId] = useState(null);
  const [useCheckboxes, setUseCheckboxes] = useState(false);
  const PreferenceControl = useCheckboxes ? Checkbox : Switch;
  const planKind = group?.planType?.split("-")[1]?.toLowerCase();
  const isLoading = isGroupLoading || !group?.planType;

  const gradeColumns = useMemo(() => {
    const allowedGrades = grades.filter(({ value }) => {
      if (planKind === "highed") {
        return value === "HigherEd" || value === "Educator";
      }
      if (planKind === "gov") {
        return value === "Gov" || value === "Educator";
      }
      return value !== "HigherEd" && value !== "Gov";
    });

    return allowedGrades.sort(
      (a, b) => Number(a.value === "Educator") - Number(b.value === "Educator")
    );
  }, [planKind]);

  const rows = preferenceFeatures.map((feature) => ({
    ...feature,
    ...draftPreferences[group?.id]?.[feature.id],
  }));
  const selectedFeature = rows.find(({ id }) => id === selectedFeatureId);

  const updatePreference = (featureId, values) => {
    setDraftPreferences((previous) => ({
      ...previous,
      [group.id]: {
        ...previous[group.id],
        [featureId]: {
          ...previous[group.id]?.[featureId],
          ...values,
        },
      },
    }));
  };

  const updateAllGrades = (featureId, checked) => {
    updatePreference(
      featureId,
      Object.fromEntries(gradeColumns.map(({ value }) => [value, checked]))
    );
  };

  const columns = [
    {
      field: "feature",
      headerName: "Feature",
      width: 280,
      minWidth: 280,
      sortable: false,
      renderCell: ({ row, value, tabIndex }) => (
        <ButtonBase
          onClick={() => setSelectedFeatureId(row.id)}
          tabIndex={tabIndex}
          aria-label={`View ${value} settings`}
          aria-haspopup="dialog"
          sx={{
            justifyContent: "flex-start",
            height: "100%",
            width: "100%",
            textAlign: "left",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
            "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: -2 },
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: "normal", lineHeight: 1.4 }}>
            {value}
          </Typography>
        </ButtonBase>
      ),
    },
    {
      field: "all",
      headerName: "All",
      width: 64,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: ({ row, tabIndex }) => {
        const allChecked = gradeColumns.every(({ value }) => Boolean(row[value]));
        const someChecked = gradeColumns.some(({ value }) => Boolean(row[value]));
        const status = allChecked ? "On" : someChecked ? "Some selected" : "Off";

        return (
          <Tooltip title={`${row.feature} for all: ${status}`}>
            <PreferenceControl
              size="small"
              checked={allChecked}
              {...(useCheckboxes && { indeterminate: someChecked && !allChecked })}
              disabled={isLoading}
              inputProps={{
                "aria-label": `${row.feature} for all`,
                tabIndex,
              }}
              onChange={(_, checked) => {
                updateAllGrades(row.id, checked);
              }}
            />
          </Tooltip>
        );
      },
    },
    ...gradeColumns.map(({ value, label }) => ({
      field: value,
      headerName: value === "HigherEd" ? "Higher Ed" : value === "Gov" ? "Government" : value,
      description: label,
      width: value === "Gov" ? 130 : value === "Educator" || value === "HigherEd" ? 110 : 64,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: ({ row, tabIndex }) => (
        <Tooltip title={`${row.feature} for ${label}: ${row[value] ? "On" : "Off"}`}>
          <PreferenceControl
            size="small"
            checked={Boolean(row[value])}
            disabled={isLoading}
            inputProps={{
              "aria-label": `${row.feature} for ${label}`,
              tabIndex,
            }}
            onChange={(_, checked) => {
              updatePreference(row.id, { [value]: checked });
            }}
          />
        </Tooltip>
      ),
    })),
  ];

  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <Stack spacing={1}>
        <Typography variant="body1">
          Which do you prefer: toggle buttons or checkboxes?
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2">Toggle buttons</Typography>
          <Switch
            checked={useCheckboxes}
            onChange={(_, checked) => setUseCheckboxes(checked)}
            inputProps={{ "aria-label": "Use checkboxes instead of toggle buttons" }}
          />
          <Typography variant="body2">Checkboxes</Typography>
        </Stack>
      </Stack>
      <Box sx={{ width: "100%", minWidth: 0 }}>
        <DataGrid
          aria-label="Group preferences by grade"
          autoHeight
          columns={columns}
          rows={isLoading ? [] : rows}
          loading={isLoading}
          density="compact"
          rowHeight={72}
          disableColumnMenu
          disableColumnFilter
          disableRowSelectionOnClick
          hideFooter
          sx={{ border: "none" }}
        />
      </Box>
      <GroupPreferenceDrawer
        feature={selectedFeature}
        gradeColumns={gradeColumns}
        disabled={isLoading}
        useCheckboxes={useCheckboxes}
        onClose={() => setSelectedFeatureId(null)}
        onChange={updatePreference}
        onChangeAll={updateAllGrades}
      />
    </Stack>
  );
}
